import { Box, Flex, Text, VStack } from '@chakra-ui/react';
import { AgentComplianceReviewByShareLinkTokenQuery } from '@client/graphql/__generated__/types';
import { FC } from 'react';
import {
  ComplianceReviewBreadcrumb,
  DocumentList,
  DocumentViewer,
  FolderSuggestionsCard,
  KeyTermsPanel,
  PropertySummaryCard,
  RightRail,
} from './components';
import { Participants } from './components/types';
import { useAgentComplianceReview } from './hooks/useAgentComplianceReview';

type AgentComplianceReviewOverview =
  AgentComplianceReviewByShareLinkTokenQuery['agentComplianceReviewByShareLinkToken'];

interface AgentComplianceReviewMobileProps {
  overview: AgentComplianceReviewOverview;
  /** Currently selected document (from the `?documentId=` query param). */
  selectedDocumentId: string | undefined;
  /** Share-link token (`?token=` query param), preserved across navigation. */
  token: string | undefined;
  /** Folder UUID (route param) the review belongs to. */
  ingestionFolderUuid: string | undefined;
}

/**
 * Mobile single-pane layout for the agent compliance review page.
 *
 * The desktop three panes don't fit a phone, so the layout becomes a flow:
 * - **Overview** (no `?documentId=`): the property summary, key-terms panel,
 *   folder suggestions, and selectable document list, full width.
 * - **Detail** (a document selected): a {@link ComplianceReviewBreadcrumb}
 *   (property → document, tap the property to go back), the
 *   {@link DocumentViewer}, then the document actions / suggestions / checks
 *   stacked below.
 *
 * All behavior comes from {@link useAgentComplianceReview}, shared with the
 * desktop layout, so selection, mutations, and derived validation stay
 * identical across breakpoints.
 */
export const AgentComplianceReviewMobile: FC<
  AgentComplianceReviewMobileProps
> = ({ overview, selectedDocumentId, token, ingestionFolderUuid }) => {
  const {
    document,
    loading,
    error,
    keyTerms,
    platformUrl,
    activeFieldId,
    validation,
    hasSelection,
    selectedDocSummary,
    selectedDocNeedsReview,
    platformRequirementIssues,
    isIgnoring,
    isIgnoringPlatformIssue,
    isSubmitting,
    defaultResubmitMessage,
    onSelectFields,
    onSelectDocument,
    onClearSelection,
    onIgnoreDocumentSuggestion,
    onUndoIgnoreDocumentSuggestion,
    onIgnoreFolderSuggestion,
    onUndoIgnoreFolderSuggestion,
    onIgnorePlatformRequirementIssue,
    onUndoIgnorePlatformRequirementIssue,
    onResubmit,
  } = useAgentComplianceReview({
    overview,
    selectedDocumentId,
    token,
    ingestionFolderUuid,
  });

  // Overview: full-width single column. The desktop "Compliance review /
  // folder name" header is dropped here (the property card carries the same
  // address) per the mobile design.
  if (!hasSelection) {
    return (
      <Box
        bg="compliance.sidebar.bg"
        h="100dvh"
        maxH="100dvh"
        overflowY="auto"
        w="full"
      >
        <VStack align="stretch" p={4} spacing={5}>
          <PropertySummaryCard
            folderName={overview.folderName}
            property={overview.property}
          />

          <KeyTermsPanel
            keyTerms={keyTerms}
            participants={
              overview.participants as Participants | null | undefined
            }
          />

          <FolderSuggestionsCard
            isIgnoring={isIgnoring}
            suggestions={overview.folderSuggestions}
            onIgnore={onIgnoreFolderSuggestion}
            onUndoIgnore={onUndoIgnoreFolderSuggestion}
          />

          <DocumentList
            documents={overview.documents}
            selectedDocumentId={selectedDocumentId}
            onSelectDocument={onSelectDocument}
          />
        </VStack>
      </Box>
    );
  }

  const propertyLabel =
    [
      overview.property?.streetAddress?.trim(),
      overview.property?.cityState?.trim(),
    ]
      .filter(Boolean)
      .join(' ') || overview.folderName;

  // Detail: breadcrumb (with back-to-overview) above the PDF, then the
  // document actions / suggestions / checks stacked below in a single scroll.
  return (
    <Flex
      bg="compliance.background.primary"
      direction="column"
      h="100dvh"
      maxH="100dvh"
      overflow="hidden"
      w="full"
    >
      <Box
        borderBottomColor="compliance.border.primary"
        borderBottomWidth={1}
        flexShrink={0}
        px={4}
        py={3}
      >
        <ComplianceReviewBreadcrumb
          documentName={
            selectedDocSummary?.documentName ?? document?.documentName
          }
          propertyLabel={propertyLabel}
          onBack={onClearSelection}
        />
      </Box>

      <Box flex={1} minH={0} overflowY="auto">
        {error ? (
          <Box p={4}>
            <Text color="compliance.notification.error.text" fontSize="sm">
              We couldn&apos;t load this document. Please try again.
            </Text>
          </Box>
        ) : (
          <>
            {/* The PDF viewer fills its parent height, so give it a fixed
                viewport-relative box; the actions/suggestions below scroll
                into view within this single scroll region. */}
            <Box h="60dvh" minH="60dvh">
              <DocumentViewer
                activeFieldId={activeFieldId}
                documentName={document?.documentName}
                loading={loading}
                pdfUrl={document?.pdfUrl}
                submittedAt={
                  document?.submittedAt != null
                    ? String(document.submittedAt)
                    : undefined
                }
                successFieldIds={validation.successFieldIds}
                validationFieldsMap={validation.validationFieldsMap}
                warningFieldIds={validation.errorFieldIds}
                warningFieldMessages={validation.fieldMessages}
              />
            </Box>

            <Box
              bg="compliance.sidebar.bg"
              borderTopColor="compliance.sidebar.border"
              borderTopWidth={1}
              p={4}
            >
              <RightRail
                defaultResubmitMessage={defaultResubmitMessage}
                hasDetail={document != null}
                hasSelection={hasSelection}
                isIgnoring={isIgnoring}
                isIgnoringPlatformRequirementIssue={isIgnoringPlatformIssue}
                isResubmitted={selectedDocSummary?.agentResubmittedAt != null}
                isSubmitting={isSubmitting}
                loading={loading}
                needsReview={selectedDocNeedsReview}
                platformRequirementIssues={platformRequirementIssues}
                platformUrl={platformUrl}
                successfulChecks={validation.successfulChecks}
                suggestions={validation.suggestions}
                onIgnorePlatformRequirementIssue={
                  onIgnorePlatformRequirementIssue
                }
                onIgnoreSuggestion={onIgnoreDocumentSuggestion}
                onResubmit={onResubmit}
                onSelectFields={onSelectFields}
                onUndoIgnorePlatformRequirementIssue={
                  onUndoIgnorePlatformRequirementIssue
                }
                onUndoIgnoreSuggestion={onUndoIgnoreDocumentSuggestion}
              />
            </Box>
          </>
        )}
      </Box>
    </Flex>
  );
};
